// @ts-strict-ignore
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { ApproveProjectFinalizationExportArgsVO, ApproveProjectFinalizationExportResultVO, ComplexProjectVO, CustomsTariffNumberVO, FinalizationPreselectionVO, ProjectArticleNumberInfoVO, ProjectFinalizationInfoVO, ProjectHeadVO, ProjectHistoryVO, ProjectSaveInfoVO, ProjectVO, RequestFinalizeProjectResultVO } from '../model/vo/project.vo';
import { WagoApiClient } from '@shared/service/wago-api.client';
import { ModelConversionService } from '@shared/convert/model-conversion.service';
import { NumberUtils } from '@shared/util/number-utils';
import { Injectable } from '@angular/core';
import { ProjectOrderResultVO } from '@article/model/vo/project-order-result.dto';
import { ProjectOrderDTO } from '@article/model/vo/project-order.dto';
import { ProjectRepresentationDataDto } from '@project/model/vo/project-representation-data.dto';
import { DateFormatter } from '@shared/date-formatter';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CompositeArticleConfigurationVO } from '@article/model/vo/composite-article-configuration.vo';
import { ArticleWithTechnicalPropertiesDTO } from '@article/properties/technical/technical-requirements';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { StringUtils } from '@shared/util/string-utils';
import { OBJRepresentationDataVO, RepresentationDataVO } from '@shared/vo/representation.vo';
import { MdmGroupVO } from '@user/model/vo/user.vo';
import { ProjectUtils } from '../components/project/util/project-utils';
import { FinalizationData } from '../components/request/processor/finalization-data';
import { ProjectSampleRequestDTO } from '../configuration-consistent-samples/project-sample-request.dto';
import { GenerationResponseVO } from '../manager/generation-response.vo';
import { ArticleNumberLong } from '../model/article-number-long';
import { BuildingBlockAssistantSearchParams, BuildingBlockHeadVO, BuildingBlockSaveDTO, BuildingBlockSaveResponseVO, BuildingBlockVO } from '../model/vo/building-block.vo';
import { ProjectFinalizationInfoDTO } from '../model/vo/project-finalization-info.dto';
import { ProjectFinalizationPreviewDTO } from '../model/vo/project-finalization-preview.dto';
import { ProjectLinkAccessVO } from '../model/vo/project-link-access-vo';

@Injectable({providedIn: 'root'})
export class ProjectDataService {
  constructor(
    private wagoApiClient: WagoApiClient,
    private modelConversionService: ModelConversionService,
    private localeResourceManager: LocaleResourceManager,
  ) {}

  public async loadComplexProjectByGuid(guid: string): Promise<ComplexProjectVO> {
    const requestOptions = { params: new HttpParams().append('guid', guid) };

    const url = 'project/complex/by-guid';
    const response = await this.wagoApiClient.getSingle(url, requestOptions, { isErrorHandlingRequired: false });
    return this.modelConversionService.convertComplexProjectVO(response);
  }

  public async loadCompositeArticleComplexProject(projectId: number, version: number): Promise<ComplexProjectVO> {
    if (!NumberUtils.isNumber(projectId) || !Number.isInteger(projectId) || !NumberUtils.isNumber(version) || !Number.isInteger(version)) {
      return;
    }
    const requestOptions = {params: new HttpParams().append('version', version.toString())};
    const url = `composite-article/project/${projectId}/complex`;
    const response = await this.wagoApiClient.getSingle(url, requestOptions);
    return this.modelConversionService.convertComplexProjectVO(response);
  }

  public async loadComplexProject(projectId: number, projectVersion: number): Promise<ComplexProjectVO> {
    if (!NumberUtils.isNumber(projectId) || !Number.isInteger(projectId) || !NumberUtils.isNumber(projectVersion) || !Number.isInteger(projectVersion)) {
      // TODO throw exception?
      return null;
    }
    const requestOptions = {params: new HttpParams().append('version', projectVersion.toString())};
    const url = `project/complex/${projectId}`;
    const response = await this.wagoApiClient.getSingle(url, requestOptions, { isErrorHandlingRequired: false });
    return this.modelConversionService.convertComplexProjectVO(response);
  }

  public async loadCompositeArticleComplexProjectByArticleId(
    articleId: number,
    articleVersion?: number,
    isErrorHandlingRequired: boolean = true,
  ): Promise<ComplexProjectVO> {
    let url: string = `composite-article/${articleId}/project/complex`;
    if (NumberUtils.isNumber(articleVersion)) {
      url = url + `?version=${articleVersion}`;
    }
    const response = await this.wagoApiClient.getSingle(url, null, {isErrorHandlingRequired: isErrorHandlingRequired});
    return this.modelConversionService.convertComplexProjectVO(response);
  }

  public async deleteConfiguration(configurationId: number): Promise<void> {
    const url = `project/${configurationId}`;
    await this.wagoApiClient.deleteSingle(url, null, {isErrorHandlingRequired: false});
  }

  public orderProject(projectOrderDTO: ProjectOrderDTO): Observable<ProjectOrderResultVO> {
    return this.wagoApiClient.post(`project/order`, projectOrderDTO, null, {
      isErrorHandlingRequired: false,
    });
  }

  public setFinalizationState(projectId: number, finalizationState: number): Observable<unknown> {
    const requestOptions = {params: new HttpParams().append('state', finalizationState.toString())};
    return this.wagoApiClient
      .post(`project/${projectId}/finalization/state`, null, requestOptions, {
        isErrorHandlingRequired: false,
      })
      .pipe(catchError((responseError: HttpErrorResponse) => throwError(() => responseError.error)));
  }

  public executeFinalizeProjectForDuplicate(
    projectId: number,
    version: number,
    projectFinalizationInfoVO: ProjectFinalizationInfoVO,
  ): Observable<void> {
    const requestOptions = {params: new HttpParams().append('version', version.toString())};
    return this.wagoApiClient.post(
      `project/${projectId}/finalization/execute`,
      projectFinalizationInfoVO,
      requestOptions,
      { isErrorHandlingRequired: false },
    );
  }

  public loadProjectData(projectId: number, projectVersion: number = -1): Observable<ProjectVO> {
    if (!NumberUtils.isNumber(projectId) || !Number.isInteger(projectId)) {
      return of(null);
    }
    const requestOptions = {params: new HttpParams().append('version', projectVersion.toString())};
    return this.wagoApiClient
      .get(`project/${projectId}/data`, requestOptions, {
        isErrorHandlingRequired: false,
        overlayType: null,
      })
      .pipe(map((response) => this.modelConversionService.convertProjectVO(response)));
  }

  public loadProjectSaveInfo(projectId: number, version: number = -1, versionSaveDate: string = null, isErrorHandlingRequired: boolean = true): Observable<ProjectSaveInfoVO> {
    if (!NumberUtils.isNumber(projectId) || !Number.isInteger(projectId)) {
      return of(null);
    }
    let httpParams: HttpParams = new HttpParams();
    if (NumberUtils.isNumber(version)) {
      httpParams = httpParams.append('version', version.toString());
    }
    if (StringUtils.isNotEmpty(versionSaveDate)) {
      httpParams = httpParams.append('versionSaveDate', versionSaveDate);
    }
    const requestOptions = {params: httpParams};
    return this.wagoApiClient
      .get(`project/${projectId}/save-info`, requestOptions, { isErrorHandlingRequired })
      .pipe(map((response) => this.modelConversionService.convertProjectSaveInfoVO(response)));
  }

  public loadProjectHistory(projectId: number): Observable<ProjectHistoryVO> {
    if (!NumberUtils.isNumber(projectId)) {
      return of(null);
    }
    const requestUrl: string = `project/${projectId}/history`;
    return this.wagoApiClient
      .get(requestUrl, null, { isErrorHandlingRequired: false })
      .pipe(map((response) => this.modelConversionService.convertProjectHistoryVO(response)));
  }

  public saveProject(
    projectId: number,
    project: ProjectVO,
    data: OBJRepresentationDataVO,
    createSubprojects: boolean,
    updateAllFields: boolean,
    errorHandlingRequired: boolean = true,
  ): Observable<ProjectVO> {
    if (!NumberUtils.isNumber(projectId) || !Number.isInteger(projectId)) {
      return of(null);
    }
    const projectWithDataDTO: ProjectRepresentationDataDto = {project, data, createSubprojects, updateAllFields};
    return this.wagoApiClient
      .post(`project/${projectId}`, projectWithDataDTO, null, {
        isErrorHandlingRequired: errorHandlingRequired,
      })
      .pipe(
        map((response) => {
          return this.modelConversionService.convertProjectVO(response);
        }),
      );
  }

  public saveProjectAsNewVersion(
    projectId: number,
    project: ProjectVO,
    data: OBJRepresentationDataVO,
    createSubprojects: boolean,
    updateAllFields: boolean,
    errorHandlingRequired: boolean = true,
  ): Observable<ProjectVO> {
    if (!NumberUtils.isNumber(projectId) || !Number.isInteger(projectId)) {
      return of(null);
    }
    const projectWithDataDTO: ProjectRepresentationDataDto = {project, data, createSubprojects, updateAllFields};
    return this.wagoApiClient
      .post(`project/${projectId}/new-version`, projectWithDataDTO, null, {
        isErrorHandlingRequired: errorHandlingRequired,
      })
      .pipe(map((response) => this.modelConversionService.convertProjectVO(response)));
  }

  public saveProjectKeepingModificationDate(
    projectId: number,
    project: ProjectVO,
    data: OBJRepresentationDataVO = null,
    errorHandlingRequired: boolean = true,
  ): Observable<ProjectVO> {
    if (!NumberUtils.isNumber(projectId) || !Number.isInteger(projectId)) {
      return of(null);
    }
    const dataProjectDTO: any = {project: project, data: data, createSubprojects: false, updateAllFields: false};
    return this.wagoApiClient
      .post(`project/${projectId}/keep-date`, dataProjectDTO, null, {
        isErrorHandlingRequired: errorHandlingRequired,
      })
      .pipe(map((response) => this.modelConversionService.convertProjectVO(response)));
  }


  public overwriteProjectVersion(
    projectId: number,
    project: ProjectVO,
    version: number,
    data: OBJRepresentationDataVO = null,
  ): Observable<ProjectVO> {
    if (!NumberUtils.isNumber(projectId) || !Number.isInteger(projectId)) {
      return of(null);
    }
    const projectWithDataDTO: ProjectRepresentationDataDto = {project, data, createSubprojects: false, updateAllFields: false};
    return this.wagoApiClient
      .post(`project/${projectId}/overwrite/${version}`, projectWithDataDTO);
  }

  public getLatestProjectVersionNumber(projectId: number): Observable<number> {
    if (!NumberUtils.isNumber(projectId) || !Number.isInteger(projectId)) {
      return of(null);
    }
    return this.wagoApiClient.get(`project/${projectId}/latest-version`);
  }

  public moveProject(projectId: number, groupId: number): Observable<ProjectHeadVO> {
    if (!NumberUtils.isNumber(projectId) || !Number.isInteger(projectId) || !NumberUtils.isNumber(groupId) || !Number.isInteger(groupId)) {
      return of(null);
    }

    const requestOptions = {params: new HttpParams().append('groupId', groupId.toString())};
    return this.wagoApiClient
      .put(`project/${projectId}/group`, null, requestOptions, {
        isErrorHandlingRequired: false,
      })
      .pipe(map((response) => this.modelConversionService.convertProjectHeadVO(response)));
  }

  public moveCompositeArticleProject(projectId: number, groupId: number): Observable<ProjectHeadVO> {
    if (!NumberUtils.isNumber(projectId) || !Number.isInteger(projectId) || !NumberUtils.isNumber(groupId) || !Number.isInteger(groupId)) {
      return of(null);
    }

    const requestOptions = {params: new HttpParams().append('targetGroupId', groupId.toString())};
    return this.wagoApiClient
      .put(`composite-article/project/${projectId}/move`, null, requestOptions, {
        isErrorHandlingRequired: false,
      })
      .pipe(map((response) => this.modelConversionService.convertProjectHeadVO(response)));
  }

  public copyProjectOrCompositeArticleProject(
    projectId: number,
    targetGroupId: number,
    copyConfigurationProperties: boolean,
    isCompositeProject: boolean,
    newName: string = null,
  ): Observable<ProjectHeadVO> {
    if (!NumberUtils.isNumber(projectId) || !Number.isInteger(projectId) || !NumberUtils.isNumber(targetGroupId) || !Number.isInteger(targetGroupId)) {
      return of(null);
    }

    let httpParams: HttpParams = new HttpParams();
    httpParams = httpParams.append('targetGroupId', targetGroupId.toString());
    httpParams = httpParams.append('copyProjectProperties', copyConfigurationProperties.toString());
    httpParams = httpParams.append('newName', newName);
    const requestOptions = {params: httpParams};
    const controllerUrl: string = isCompositeProject ? 'composite-article/project/' : 'project/';
    return this.wagoApiClient
      .put(controllerUrl + projectId + '/copy', null, requestOptions, { isErrorHandlingRequired: false })
      .pipe(map((response) => this.modelConversionService.convertProjectHeadVO(response)));
  }

  public requestFinalizeProject(
    projectId: number,
    version: number,
    emailAddress: string,
    batchSize: number,
    deliveryDate: Date,
    remarks: string,
    sapProjectNumber: string,
    totalDemand: number,
    forceRequest: boolean,
    localeKey: string,
    orderDirectly: boolean,
    additionalEmails: string[],
  ): Observable<RequestFinalizeProjectResultVO> {
    const projectFinalizeDTO = {
      version,
      emailAddress,
      batchSize,
      deliveryDate,
      remarks,
      sapProjectNumber,
      totalDemand,
      forceRequest,
      localeKey,
      orderDirectly,
      additionalEmails,
    };
    return this.wagoApiClient
      .post(`project/${projectId}/finalization/request`, projectFinalizeDTO, null, {
        isErrorHandlingRequired: false,
      })
      .pipe(map((response) => this.modelConversionService.convertRequestFinalizeProjectResultVO(response)));
  }

  public requestFinalizeMultipleProjects(finalizationData: FinalizationData, localeKey: string): Observable<any> {
    const multipleProjectsFinalizeDto = {
      projectIdentities: ProjectUtils.generateProjectIdentities(finalizationData.projectsForMultipleRequest),
      emailAddress: finalizationData.emailAddress,
      batchSize: finalizationData.batchSize,
      deliveryDate: finalizationData.deliveryDate,
      remarks: finalizationData.remarks,
      totalDemand: finalizationData.totalDemand,
      localeKey,
      orderDirectly: finalizationData.orderDirectly,
      additionalEmails: finalizationData.additionalEmails,
    };

    return this.wagoApiClient.post(
      `project/finalization/request`,
      multipleProjectsFinalizeDto,
      null,
      { isErrorHandlingRequired: false },
    );
  }

  public generateArticleNumber(project: ProjectVO, useDefaultSeries: boolean): Observable<ProjectArticleNumberInfoVO> {
    return this.wagoApiClient
      .post(`project/finalization/generate/articlenumber/${useDefaultSeries}`, project)
      .pipe(
        map((response) => {
          return this.modelConversionService.convertProjectArticleNumberInfoVO(response);
        }),
      );
  }

  public finalizeProject(
    projectId: number,
    version: number,
    articleNumber: string,
    drawingNumber: string,
    articleNumberExtension: string,
    articleNumberLong: ArticleNumberLong,
    typeClassId: string,
    fNumber: string,
    documentVersion: number,
    legacyFinalization: boolean = false,
    useCustomArticleNumber: boolean = false,
    countryCodeString: string = null,
    useSeriesForDrawingNumber: boolean = false,
    tcVersion: number = -1,
    onlyDrawing: boolean = false,
    mdmGroup: MdmGroupVO = null,
  ): Observable<ProjectFinalizationInfoVO> {
    if (!NumberUtils.isNumber(projectId) || !Number.isInteger(projectId)) {
      return of(null);
    }
    const projectArticleDTO = {
      version,
      articleNumber,
      drawingNumber,
      articleNumberExtension,
      articleNumberLong,
      typeClassId,
      fNumber,
      documentVersion,
      legacyFinalization,
      useCustomArticleNumber,
      countryCodeString,
      useSeriesForDrawingNumber,
      tcVersion,
      onlyDrawing,
      mdmGroup,
    };
    return this.wagoApiClient
      .post(`project/${projectId}/finalization/info`, projectArticleDTO)
      .pipe(
        map((response) => {
          return this.modelConversionService.convertProjectFinalizationInfoVO(response);
        }),
      );
  }

  public getAdditionalProjectData(projectId: number): Observable<ProjectFinalizationInfoVO> {
    return this.wagoApiClient
      .get(`project/${projectId}/additional-data`)
      .pipe(map((response) => this.modelConversionService.convertProjectFinalizationInfoVO(response)));
  }

  public changeProjectOwner(projectId: number, username: string, isCreateNewVersion: boolean): Observable<ProjectHeadVO> {
    if (!NumberUtils.isNumber(projectId) || !Number.isInteger(projectId)) {
      return of(null);
    }
    return this.wagoApiClient
      .put(`project/${projectId}/owner/?username=${username}&isCreateNewVersion=${isCreateNewVersion}`, null)
      .pipe(map((response) => this.modelConversionService.convertProjectHeadVO(response)));
  }

  public renameProject(projectId: number, projectName: string): Observable<ProjectHeadVO> {
    if (NumberUtils.isNotNumber(projectId) || !projectName) {
      return of(null);
    }

    return this.wagoApiClient
      .put(`project/${projectId}/rename`, { value: projectName }, null, {
        isErrorHandlingRequired: false,
      })
      .pipe(map((response) => this.modelConversionService.convertProjectHeadVO(response)));
  }

  public saveCompositeArticle(compositeConfiguration: CompositeArticleConfigurationVO, project: ProjectVO, data: RepresentationDataVO): Observable<ProjectVO> {
    const dto: any = {compositeConfiguration, project, data};

    return this.wagoApiClient
      .post('composite-article/', dto)
      .pipe(map((response) => this.modelConversionService.convertProjectVO(response)));
  }

  public saveBuildingBlock(buildingBlockSaveDTO: BuildingBlockSaveDTO): Observable<BuildingBlockSaveResponseVO> {
    return this.wagoApiClient
      .post(`building-block/save`, buildingBlockSaveDTO)
      .pipe(map((response) => this.modelConversionService.convertBuildingBlockSaveResponseVO(response)));
  }

  public loadBuildingBlockByName(buildingBlockName: string): Observable<BuildingBlockVO> {
    return this.wagoApiClient
      .post(`building-block/by-name`, { value: buildingBlockName })
      .pipe(map((response) => this.modelConversionService.convertBuildingBlockVO(response)));
  }

  public renameCompositeArticleProject(projectId: number, projectName: string): Observable<ProjectHeadVO> {
    if (NumberUtils.isNotNumber(projectId) || !projectName) {
      return of(null);
    }

    return this.wagoApiClient
      .put(`composite-article/project/${projectId}/rename`, { value: projectName }, null, {
        isErrorHandlingRequired: false,
      })
      .pipe(map((response) => this.modelConversionService.convertProjectHeadVO(response)));
  }

  public getBuildingBlockHeadByName(buildingBlockName: string): Observable<BuildingBlockHeadVO> {
    return this.wagoApiClient
      .post(`building-block/head/by-name/`, { value: buildingBlockName })
      .pipe(map((response) => this.modelConversionService.convertBuildingBlockHeadVO(response)));
  }

  public loadBuildingBlockByParams(searchParams: BuildingBlockAssistantSearchParams): Observable<string> {
    return this.wagoApiClient.post(`building-block/name/by-params`, searchParams, {responseType: 'text'});
  }

  public checkFinalizationPreviewGenerationResult(dto: ProjectFinalizationInfoDTO): Observable<GenerationResponseVO> {
    if (!dto) {
      const error: Error = new Error('Error: ProjectService: dto is not valid: ' + dto);
      const data: any = { error: error, reportId: dto.reportId, dto: dto };
      return throwError(data);
    }
    return this.wagoApiClient
      .post(`project/${dto.projectId}/finalization/preview/check`, dto, undefined, {
        isErrorHandlingRequired: false,
        overlayType: null,
      })
      .pipe(map((object: any) => this.modelConversionService.convertGenerationResponseVO(object)));
  }

  public createFinalizationPreview(projectId: number, dto: ProjectFinalizationPreviewDTO): Observable<GenerationResponseVO> {
    return this.wagoApiClient
      .post(`project/${projectId}/finalization/preview/generate`, dto, undefined, {
        isErrorHandlingRequired: false,
      })
      .pipe(map((object: any) => this.modelConversionService.convertGenerationResponseVO(object)));
  }

  public approveProjectFinalizationExport(projectId: number, version: number, projectFinalizationInfoVO: ProjectFinalizationInfoVO, releaseDate: Date): Observable<ApproveProjectFinalizationExportResultVO> {
    const requestOptions = {
      params: new HttpParams().append('version', version.toString()),
    };
    const body: ApproveProjectFinalizationExportArgsVO = {
      projectFinalizationInfo: projectFinalizationInfoVO,
      releaseDate: DateFormatter.formatOptionDate(releaseDate),
    };

    return this.wagoApiClient.post<ApproveProjectFinalizationExportResultVO>(
      `project/${projectId}/finalization/export/approve`,
      body,
      requestOptions,
      { isErrorHandlingRequired: false },
    );
  }

  public declineProjectFinalizationExport(projectId: number, version: number, articleNumberLong: string): Observable<any> {
    let httpParams: HttpParams = new HttpParams();
    httpParams = httpParams.append('version', version.toString());
    httpParams = httpParams.append('articleNumberLong', articleNumberLong);

    const requestOptions = {params: httpParams};

    return this.wagoApiClient.post(
      `project/${projectId}/finalization/export/decline`,
      undefined,
      requestOptions,
      { isErrorHandlingRequired: false },
    );
  }

  public getLastSuccessfulFinalizationCompleteState(projectId: number): Observable<number> {
    if (!NumberUtils.isNumber(projectId) || !Number.isInteger(projectId)) {
      return of(null);
    }
    return this.wagoApiClient.get(`project/${projectId}/finalization/state`);
  }

  public lockProject(projectId: number): Observable<boolean> {
    if (!NumberUtils.isNumber(projectId) || !Number.isInteger(projectId)) {
      return of(null);
    }
    return this.wagoApiClient.get(`project/${projectId}/lock`);
  }

  public unlockProject(projectId: number): Observable<boolean> {
    if (!NumberUtils.isNumber(projectId) || !Number.isInteger(projectId)) {
      return of(null);
    }
    return this.wagoApiClient.get(`project/${projectId}/unlock`);
  }

  public finalizeProjectWithoutPDM(projectId: number, version: number): Observable<string> {
    if (!NumberUtils.isNumber(projectId) || !Number.isInteger(projectId)) {
      return of(null);
    }
    const requestOptions = {params: new HttpParams().append('version', version.toString()), responseType: 'text'};
    return this.wagoApiClient.post(
      `project/${projectId}/finalization/without-pdm/execute`,
      null,
      requestOptions,
    );
  }

  public saveAdditionalProjectData(projectId: number, projectFinalizationInfoVO: ProjectFinalizationInfoVO): Observable<any> {
    return this.wagoApiClient.post(
      `project/${projectId}/additional-data`,
      projectFinalizationInfoVO,
      null,
      { isErrorHandlingRequired: false },
    );
  }

  public getAllTariffNumbers(): Observable<CustomsTariffNumberVO[]> {
    return this.wagoApiClient
      .get(`project/tariff-no/all`)
      .pipe(map((response) => this.modelConversionService.convertTariffNumbers(response)));
  }

  public getFinalizationPreselection(projectId: number, projectVersion: number, searchAdditionalData: boolean): Observable<FinalizationPreselectionVO> {
    if (NumberUtils.isNotNumber(projectId) || NumberUtils.isNotNumber(projectVersion)) {
      return of(null);
    }

    const requestUrl: string = `project/${projectId}/finalization/preselection?projectVersion=${projectVersion}&searchAdditionalData=${searchAdditionalData}`;
    return this.wagoApiClient
      .get(requestUrl, null, { isErrorHandlingRequired: false })
      .pipe(map((response) => this.modelConversionService.convertFinalizationPreselection(response)));
  }

  public createProjectLinkAccess(projectId: number, version: number): Observable<ProjectLinkAccessVO> {
    const requestOptions = {params: new HttpParams().append('version', version.toString())};
    return this.wagoApiClient
      .get(`project/${projectId}/create-link-access/`, requestOptions)
      .pipe(map((response) => this.modelConversionService.convertProjectLinkAccessVO(response)));
  }

  public changeProjectPinning(projectId: number, version: number, isPinned: boolean, projectLinkGuid: string): Observable<boolean> {
    if (!NumberUtils.isNumber(projectId)) {
      return of(false);
    }
    let httpParams: HttpParams = new HttpParams();
    httpParams = httpParams.append('version', version.toString());
    httpParams = httpParams.append('isPinned', isPinned.toString());
    if (projectLinkGuid) {
      httpParams = httpParams.append('guid', projectLinkGuid);
    }
    const requestOptions = {
      params: httpParams,
    };
    return this.wagoApiClient.put(`project/${projectId}/pinned`, null, requestOptions);
  }

  public loadRecentlyUsedProjects(): Observable<ProjectHeadVO[]> {
    return this.wagoApiClient
      .get(`project/recently-used`)
      .pipe(map((response) => this.modelConversionService.convertListProjectHeadVO(response)));
  }

  public resetProjectFinalization(projectId: number): Observable<ProjectHeadVO> {
    return this.wagoApiClient.post(`project/${projectId}/finalization/reset`, null)
      .pipe(
        map((response) => this.modelConversionService.convertProjectHeadVO(response))
      );
  }

  public getPreferredSamples(projectId: number, propertyNames: string[]): Observable<ArticleWithTechnicalPropertiesDTO[]> {
    const langKey = this.localeResourceManager.currentLanguage.langKey;
    return this.wagoApiClient.post<ArticleWithTechnicalPropertiesDTO[]>(`project/${projectId}/preferred-samples?langKey=${langKey}`, propertyNames);
  }

  public sendConfigurationConsistentSampleRequest(projectId: number, projectSampleRequestDTO: ProjectSampleRequestDTO): Observable<boolean> {
    return this.wagoApiClient.post<boolean>(`project/${projectId}/configuration-consistent-sample`, projectSampleRequestDTO);
  }
}
